import { Component, EventEmitter, forwardRef, Injector, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { CustomAttributeSection, CustomAttributeSectionInput } from '../../entities/customAttributes/customAttributes';
import { MenuItem } from 'primeng/api';
import { CustomAttributesService } from '../../services/customAttributes/customAttributesService';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../services/toast/toast.service';
import { LoadingService } from '../../services/loading.service';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { CustomAttributeSectionDialogComponent } from '../custom-attribute-section-dialog/custom-attribute-section-dialog.component';
import { EditDialogService } from '../editDialog/editDialogService';
import { CustomAttributeDefinitionDialogComponent } from '../custom-attribute-definition-dialog/custom-attribute-definition-dialog.component';

@Component({
    selector: 'app-custom-attribute-section',
    template: `
    <div class="px-5 py-3 border-b border-gray bg-white">
        <div class="flex justify-between items-center mb-2">
            <div class="flex-1 text-left">
                {{_customAttributeSection.label}}
            </div>
            <div class="flex-none text-right">
                <nx-default-button
                    [menuItems]="menuItems"
                    [menuWithIcon]="false"
                    class="pt-1"
                    [icon]="'icon-options'">
                </nx-default-button>
            </div>
        </div>
        <div class="space-y-1 ml-2">
            <nx-empty-box
                *ngIf="!_customAttributeSection?.customAttributeDefinitions || _customAttributeSection?.customAttributeDefinitions.length === 0"
                label="coreModuleModule.customAttributeSectionComponent.customAttributeDefinitions.emptyMessage">
            </nx-empty-box>
            <ng-container *ngFor="let cad of _customAttributeSection?.customAttributeDefinitions">
                <app-custom-attribute-definition
                    [customAttributeDefinition]="cad"
                    [customAttributeDefinitions]="_customAttributeSection?.customAttributeDefinitions"
                    (refetch)="refetch.emit()">
                </app-custom-attribute-definition>
            </ng-container>
        </div>
    </div>
    `,
    styles: [`

    `]
})
export class CustomAttributeSectionComponent {

    dialogRef: DynamicDialogRef | undefined;

    public menuItems: MenuItem[] = [];
    public _customAttributeSection: CustomAttributeSection;
    public _customAttributeSections: CustomAttributeSection[];

    @Input() set customAttributeSection(customAttributeSection: CustomAttributeSection) {
        this._customAttributeSection = customAttributeSection;
        this.setMenuItems();
    };

    @Input() set customAttributeSections(customAttributeSections: CustomAttributeSection[]) {
        this._customAttributeSections = customAttributeSections;
        this.setMenuItems();
    }

    @Output() refetch: EventEmitter<any> = new EventEmitter();

    constructor(
        private customAttributesService: CustomAttributesService,
        private confirmDialogService: ConfirmDialogService,
        private translate: TranslateService,
        private toastService: ToastService,
        private loadingService: LoadingService,
        public dialogService: EditDialogService
    ) {
    }

    setMenuItems() {
        this.menuItems = [];
        if (!this._customAttributeSection) {
            return;
        }
        this.menuItems.push({
            label: this.translate.instant('coreModuleModule.customAttributeSectionComponent.menu.update'),
            disabled: false,
            command: () => {
                this.updateCustomAttributeSection();
            }
        });
        this.menuItems.push({
            label: this.translate.instant('coreModuleModule.customAttributeSectionComponent.menu.delete'),
            disabled: !this.isLast(),
            command: () => {
                this.deleteCustomAttributeSection();
            }
        });

        this.menuItems.push({
            label: this.translate.instant('coreModuleModule.customAttributeSectionComponent.menu.moveUp'),
            disabled: this.isMoveUpDisabled(),
            command: () => {
                this.moveUp();
            }
        });

        this.menuItems.push({
            label: this.translate.instant('coreModuleModule.customAttributeSectionComponent.menu.moveDown'),
            disabled: this.isMoveDownDisabled(),
            command: () => {
                this.moveDown();
            }
        });

        this.menuItems.push({
            label: this.translate.instant('coreModuleModule.customAttributeSectionComponent.menu.createCustomAttributeDefinition'),
            disabled: false,
            command: () => {
                this.createCustomAttributeDefinition();
            }
        });
    }

    isLast(): boolean {
        if (!this._customAttributeSection || !this._customAttributeSections) {
            return true;
        }
        return this._customAttributeSection.index === this._customAttributeSections.length - 1;
    }

    isMoveUpDisabled(): boolean {
        if (!this._customAttributeSection) {
            return true;
        }
        return this._customAttributeSection.index === 0;
    }

    // move down is disabled if the current index is the last index in the list
    isMoveDownDisabled(): boolean {
        if (!this._customAttributeSection || !this._customAttributeSections) {
            return true;
        }
        return !(this._customAttributeSection.index < this._customAttributeSections.length - 1);
    }

    /**
     * moves the current custom attribute section up in the list of custom attribute sections.
     * This means: Swap the current section with the section that comes after it in the array.
     * @returns
     */
    moveDown(): void {
        if (!this._customAttributeSection || !this._customAttributeSections) {
            return;
        }
        const currentIndex = this._customAttributeSection.index;
        const nextIndex = currentIndex + 1;
        // find the next section
        const nextSection = this._customAttributeSections.find(section => section.index === nextIndex);
        if (!nextSection) {
            return;
        }
        // we must swap the indices by issuing an update mutation
        const currentSectionInput = new CustomAttributeSectionInput();
        currentSectionInput.id = this._customAttributeSection.id;
        currentSectionInput.label = this._customAttributeSection.label;
        currentSectionInput.index = nextIndex;
        currentSectionInput.externalId = this._customAttributeSection.externalId;
        currentSectionInput.entity = this._customAttributeSection.entity;
        const nextSectionInput = new CustomAttributeSectionInput();
        nextSectionInput.id = nextSection.id;
        nextSectionInput.label = nextSection.label;
        nextSectionInput.externalId = nextSection.externalId;
        nextSectionInput.index = currentIndex;
        nextSectionInput.entity = nextSection.entity;
        this.loadingService.setLoading(true);
        this.customAttributesService.update(currentSectionInput, () => {
            this.customAttributesService.update(nextSectionInput, () => {
                this.refetch.emit();
                this.loadingService.setLoading(false);
                this.toastService.addToastWithMessage('coreModuleModule.customAttributeSectionComponent.customAttributeSectionMovedDownToastMessage');
            });
        });
    }

    /**
     * moves the current custom attribute section up in the list of custom attribute sections.
     * This means: Swap the current section with the section that comes before it in the array.
     * @returns
     */
    moveUp(): void {
        if (!this._customAttributeSection || !this._customAttributeSections) {
            return;
        }
        const currentIndex = this._customAttributeSection.index;
        const previousIndex = currentIndex - 1;
        // find the previous section
        const previousSection = this._customAttributeSections.find(section => section.index === previousIndex);
        if (!previousSection) {
            return;
        }
        // we must swap the indices by issuing an update mutation
        const currentSectionInput = new CustomAttributeSectionInput();
        currentSectionInput.id = this._customAttributeSection.id;
        currentSectionInput.label = this._customAttributeSection.label;
        currentSectionInput.externalId = this._customAttributeSection.externalId;
        currentSectionInput.index = previousIndex;
        currentSectionInput.entity = this._customAttributeSection.entity;
        const previousSectionInput = new CustomAttributeSectionInput();
        previousSectionInput.id = previousSection.id;
        previousSectionInput.label = previousSection.label;
        previousSectionInput.externalId = previousSection.externalId;
        previousSectionInput.index = currentIndex;
        previousSectionInput.entity = previousSection.entity;
        this.loadingService.setLoading(true);
        this.customAttributesService.update(currentSectionInput, () => {
            this.customAttributesService.update(previousSectionInput, () => {
                this.refetch.emit();
                this.loadingService.setLoading(false);
                this.toastService.addToastWithMessage('coreModuleModule.customAttributeSectionComponent.customAttributeSectionMovedUpToastMessage');
            });
        });
    }

    deleteCustomAttributeSection(): void {
        this.dialogRef = this.confirmDialogService.open({
            message: this.translate.instant(
            'coreModuleModule.customAttributeSectionComponent.deleteCustomAttributeSectionDialogMessage',
            { customAttributeSectionId: this._customAttributeSection?.id }
            ),
            severity: ConfirmDialogSeverity.WARN,
            header: 'coreModuleModule.customAttributeSectionComponent.deleteCustomAttributeSectionDialogHeader',
        });
        this.dialogRef.onClose.subscribe((result: ConfirmDialogEventType) => {
            if (result === ConfirmDialogEventType.CONFIRM) {
            this.loadingService.setLoading(true);
            this.customAttributesService.delete(this._customAttributeSection?.id, () => {
                this.refetch.emit();
                this.toastService.addToastWithMessage('coreModuleModule.customAttributeSectionComponent.customAttributeSectionDeletedToastMessage');
            });
            };
        })
    }

    updateCustomAttributeSection(): void {
        this.dialogRef = this.dialogService.open(CustomAttributeSectionDialogComponent, {
            header: 'coreModuleModule.customAttributeSectionComponent.updateCustomAttributeSectionDialogHeader',
            data: {
                customAttributeSection: this._customAttributeSection
            }
        });
        this.dialogRef.onClose.subscribe((result) => {
            // result is true or false, indiciating if it should refetch
            if (result) {
                this.refetch.emit();
                this.toastService.addToastWithMessage('coreModuleModule.customAttributeSectionComponent.customAttributeSectionUpdatedToastMessage');
            }
        });
    }

    createCustomAttributeDefinition(): void {
        this.dialogRef = this.dialogService.open(CustomAttributeDefinitionDialogComponent, {
            header: 'coreModuleModule.customAttributeSectionComponent.createCustomAttributeDefinitionDialogHeader',
            data: {
                customAttributeSectionId: this._customAttributeSection?.id,
                nextIndex: this._customAttributeSection?.customAttributeDefinitions?.length || 0,
                entityType: this._customAttributeSection?.entity,
            }
        });
        this.dialogRef.onClose.subscribe((result) => {
            // result is true or false, indiciating if it should refetch
            if (result) {
                this.refetch.emit();
                this.toastService.addToastWithMessage('coreModuleModule.customAttributeSectionComponent.customAttributeDefinitionCreatedToastMessage');
            }
        });
    }

}
